import type { RegisterInput } from "@xop-any-time/shared";
import { countryOptions } from "@xop-any-time/shared";
import { Box, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useId, useState, type FormEvent, type JSX } from "react";
import { Link, Navigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider.js";
import { ApiError, api } from "../lib/api.js";
import { AuthCard, AuthNotice, SubmitButton } from "./login/AuthCard.js";
import { Field } from "./login/Field.js";
import { PAGE_META } from "./public/content.js";
import { usePageMeta } from "./public/usePageMeta.js";

/**
 * `/register` — a company applying to become an XOP reseller (spec E §10.1).
 *
 * The sign-in screen's chrome, minus the globe (like the invitation accept
 * page) — now `PublicLayout`'s, shared with the rest of the public site:
 * this is a task, and the two-column hero would push an eight-field form
 * below the fold on a phone. No password field — the emailed link sets it,
 * which doubles as the email verification.
 *
 * THE CONFIRMATION NEVER VARIES. `POST /api/register` answers 202 for a new
 * registration, a repeat, an address that is already a reseller, one that
 * belongs to a staff or customer account, and an identity-plane failure — so
 * this page cannot know which happened and must not pretend to. Saying
 * "that address is already registered" would make the form an oracle for
 * which companies XOP resells through. A 4xx/5xx is a genuine refusal and
 * renders as an error, with the typed values kept.
 */

export const REGISTER_CONFIRMATION =
  "Thanks — if that address can be used, an email with your next step is on its way.";

type Values = Required<RegisterInput>;

const EMPTY: Values = {
  companyName: "",
  website: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  country: "",
  message: "",
};

/** The shared list the Settings page's country picker uses. */
const COUNTRIES: readonly string[] = countryOptions();

/** Blank optional fields are OMITTED, never sent as `""`. */
function toInput(v: Values): RegisterInput {
  return {
    companyName: v.companyName.trim(),
    firstName: v.firstName.trim(),
    lastName: v.lastName.trim(),
    email: v.email.trim(),
    ...(v.website.trim() ? { website: v.website.trim() } : {}),
    ...(v.phone.trim() ? { phone: v.phone.trim() } : {}),
    ...(v.country.trim() ? { country: v.country.trim() } : {}),
    ...(v.message.trim() ? { message: v.message.trim() } : {}),
  };
}

export function RegisterPage(): JSX.Element {
  const { user, applicant } = useAuth();
  const countriesId = useId();
  const [values, setValues] = useState<Values>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Values, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  usePageMeta(PAGE_META.register);

  // Hooks above, redirects below — a conditional return above a hook is the
  // "Rendered more hooks" crash.
  if (user) return <Navigate to="/" replace />;
  if (applicant) return <Navigate to="/onboarding" replace />;

  const set = (name: keyof Values) => (value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((e) => ({ ...e, [name]: undefined }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await api<{ ok: true }>("/api/register", { method: "POST", body: toInput(values) });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields as Partial<Record<keyof Values, string>>);
      } else {
        setError("We couldn't send your application just now. Please try again in a moment.");
      }
    } finally {
      // Values are kept on failure: a transient error must not cost someone
      // eight fields of typing.
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="div"
      sx={{
        position: "relative",
        zIndex: 1,
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxWidth: 1380,
        mx: "auto",
        px: { xs: 2.5, sm: 5 },
        py: { xs: 5, lg: 7 },
      }}
    >
      {done ? (
        <AuthCard title="Application received" description={REGISTER_CONFIRMATION}>
          <Typography component="p" sx={{ mt: 3, fontSize: 13, lineHeight: 1.6, color: "text.secondary" }}>
            The email comes from XOP Networks and contains a link to set your password.
            After that you'll be asked to sign the Reseller Agreement and NDA.
          </Typography>
          <BackToSignIn />
        </AuthCard>
      ) : (
        <AuthCard
          title="Apply to become a reseller"
          description="Tell us about your company. If the address can be used, we'll email you a link to set your password and continue."
          footer={
            <>
              Already a partner?{" "}
              <Box component={Link} to="/login" sx={linkSx}>
                Sign in
              </Box>
              .
            </>
          }
        >
          <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3.25, display: "grid", gap: 2 }}>
            {error && <AuthNotice>{error}</AuthNotice>}

            <Field
              id="companyName"
              label="Company name"
              autoComplete="organization"
              value={values.companyName}
              onChange={set("companyName")}
              required
              autoFocus
              maxLength={200}
              disabled={submitting}
              {...(fieldErrors.companyName ? { error: fieldErrors.companyName } : {})}
            />
            <Field
              id="website"
              label="Website"
              type="url"
              autoComplete="url"
              placeholder="https://"
              value={values.website}
              onChange={set("website")}
              maxLength={255}
              disabled={submitting}
              {...(fieldErrors.website ? { error: fieldErrors.website } : {})}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Field
                id="firstName"
                label="First name"
                autoComplete="given-name"
                value={values.firstName}
                onChange={set("firstName")}
                required
                maxLength={80}
                disabled={submitting}
                {...(fieldErrors.firstName ? { error: fieldErrors.firstName } : {})}
              />
              <Field
                id="lastName"
                label="Last name"
                autoComplete="family-name"
                value={values.lastName}
                onChange={set("lastName")}
                required
                maxLength={80}
                disabled={submitting}
                {...(fieldErrors.lastName ? { error: fieldErrors.lastName } : {})}
              />
            </Box>
            <Field
              id="email"
              label="Work email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={values.email}
              onChange={set("email")}
              required
              maxLength={255}
              disabled={submitting}
              {...(fieldErrors.email ? { error: fieldErrors.email } : {})}
            />
            <Field
              id="phone"
              label="Phone"
              type="tel"
              autoComplete="tel"
              value={values.phone}
              onChange={set("phone")}
              maxLength={40}
              disabled={submitting}
              {...(fieldErrors.phone ? { error: fieldErrors.phone } : {})}
            />
            {/* A native combo box over the SHARED country list: the same
                `countryOptions()` the Settings page's picker offers, as a
                datalist so the field stays in this card's bare-input family
                rather than pulling MUI's Autocomplete onto the front door. */}
            <Field
              id="country"
              label="Country"
              autoComplete="country-name"
              list={countriesId}
              value={values.country}
              onChange={set("country")}
              maxLength={80}
              disabled={submitting}
              {...(fieldErrors.country ? { error: fieldErrors.country } : {})}
            />
            <datalist id={countriesId}>
              {COUNTRIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <TextArea
              id="message"
              label="Anything we should know?"
              value={values.message}
              onChange={set("message")}
              maxLength={2000}
              disabled={submitting}
              {...(fieldErrors.message ? { error: fieldErrors.message } : {})}
            />

            <SubmitButton disabled={submitting}>{submitting ? "Sending…" : "Apply"}</SubmitButton>
          </Box>
        </AuthCard>
      )}
    </Box>
  );
}

const linkSx = {
  color: (t: Theme) => t.palette.accent[500],
  fontWeight: 500,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline", textUnderlineOffset: 3 },
} as const;

function BackToSignIn(): JSX.Element {
  return (
    <Box
      component={Link}
      to="/login"
      sx={{
        mt: 3.5,
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1.5,
        border: 1,
        borderColor: "divider",
        fontSize: 14,
        fontWeight: 500,
        textDecoration: "none",
        color: "text.primary",
        "&:hover": {
          bgcolor: (t) => alpha(t.palette.accent[500], 0.08),
          borderColor: (t) => alpha(t.palette.accent[500], 0.4),
        },
      }}
    >
      Back to sign-in
    </Box>
  );
}

/** `Field`'s multi-line sibling: same label row, same frame, a textarea. */
function TextArea({
  id,
  label,
  error,
  value,
  onChange,
  maxLength,
  disabled,
}: {
  id: string;
  label: string;
  error?: string;
  value: string;
  onChange: (v: string) => void;
  maxLength: number;
  disabled: boolean;
}): JSX.Element {
  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={{ display: "block", mb: 0.75, fontSize: 12.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box
        component="textarea"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        disabled={disabled}
        rows={3}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        sx={{
          width: "100%",
          minHeight: 88,
          resize: "vertical",
          borderRadius: 1.5,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          px: 1.75,
          py: 1.25,
          fontFamily: "inherit",
          fontSize: 14.5,
          letterSpacing: "-0.01em",
          color: "text.primary",
          outline: "none",
          transition: "border-color 150ms, box-shadow 150ms",
          "&:focus": (t) => ({
            borderColor: t.palette.accent[500],
            boxShadow: `0 0 0 2px ${alpha(t.palette.accent[500], 0.2)}`,
          }),
        }}
      />
      {error && (
        <Typography id={`${id}-error`} component="p" sx={{ mt: 0.75, fontSize: 12, fontWeight: 500, color: "error.main" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
